'use strict'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Paper,
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Collapse,
  IconButton,
  Divider
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CloseIcon from '@material-ui/icons/Close'
import MapIcon from '@material-ui/icons/Map'
import { LoadScript, GoogleMap, Marker } from '@react-google-maps/api'
import { getCrewColor } from './crewColors'

const ALL_SECTORS = ['E', 'NE', 'N', 'NW', 'W', 'SW', 'S', 'SE']

const REASON_LABELS = {
  weekly: { label: 'Weekly', tooltip: 'Serviced 48+ times per year (+5 pts)' },
  isolated: { label: 'Isolated', tooltip: 'No other weekly site within 3 miles (+2 pts)' },
  vip: { label: 'VIP', tooltip: 'VIP / priority client (+5 pts)' },
  profitable: { label: 'Profitable', tooltip: 'High margin property (+3 pts)' },
  unprofitable: { label: 'Unprofitable', tooltip: 'Operating at a loss (legacy signal, no current score boost)' },
  underestimated: { label: 'Underestimated', tooltip: 'Time consistently underestimated (+2 pts)' },
  manual_selected: { label: 'Manual', tooltip: 'Manually promoted by admin' }
}

function getReasonLabel (code) {
  if (REASON_LABELS[code]) return REASON_LABELS[code]
  if (/h_onsite$/.test(code)) {
    return { label: 'Long service', tooltip: `${code.replace('_onsite', '').replace('>=', '')} hours per visit (+4 pts)` }
  }
  if (/mi_from_yard$/.test(code)) {
    return { label: 'Far from yard', tooltip: `${code.replace('_from_yard', '').replace('>=', '')} miles from yard (+3 pts)` }
  }
  return { label: code, tooltip: code }
}

function getFrequencyLabel (item = {}) {
  if (item.frequencyLabel) return item.frequencyLabel
  const code = String(item.frequencyCode || '').trim().toUpperCase()
  const map = {
    D: 'Daily',
    MWF: 'Every Other Day (M-W-F)',
    TW: 'Twice a Week',
    W: 'Weekly',
    BW: 'Bi-Weekly',
    BW_A: 'Bi-Weekly (Week A)',
    BW_B: 'Bi-Weekly (Week B)',
    M: 'Monthly',
    Q: 'Quarterly',
    SA: 'Semi-Annually',
    A: 'Annually',
    UNKNOWN: 'Unknown'
  }
  return map[code] || 'Unknown'
}

function isWeeklyAnchorEligible (item = {}) {
  if (typeof item.weeklyAnchorEligible === 'boolean') return item.weeklyAnchorEligible
  const code = String(item.frequencyCode || '').trim().toUpperCase()
  return code === 'W'
}

function isSuggestedAnchorViewItem (item = {}) {
  if (typeof item.suggestedAnchor === 'boolean') return item.suggestedAnchor
  return item.isAnchor === true || (isWeeklyAnchorEligible(item) && item.isAnchorCandidate)
}

function getCandidateMarkerColor (score) {
  const n = Number(score || 0)
  if (n >= 18) return '#1b5e20'
  if (n >= 15) return '#2e7d32'
  if (n >= 12) return '#7cb342'
  if (n >= 10) return '#f9a825'
  if (n >= 7) return '#ef6c00'
  return '#9e9e9e'
}

function getCandidateMarkerScale (score) {
  const n = Number(score || 0)
  if (n >= 18) return 11
  if (n >= 15) return 10
  if (n >= 12) return 9
  if (n >= 10) return 8
  return 7
}

const WORKFLOW_STEPS = [
  { num: 1, label: 'Score' },
  { num: 2, label: 'Promote Anchors' },
  { num: 3, label: 'Assign Crews' },
  { num: 4, label: 'Compute Centroids' }
]
const ANCHOR_VIEW_MODES = {
  suggested: 'suggested',
  weekly: 'weekly',
  all: 'all'
}

const ANCHOR_MAP_STYLE = { width: '100%', height: 400, minHeight: 300 }
const DEFAULT_CENTER = { lat: 33.45, lng: -112.07 }
const DEFAULT_ZOOM = 10

const useStyles = makeStyles(theme => ({
  controlsCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  controlsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    marginBottom: theme.spacing(1.5)
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  table: {
    minWidth: 650
  },
  cell: {
    padding: '6px 10px'
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    padding: '8px 10px'
  },
  addressText: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary
  },
  crewSelect: {
    minWidth: 160
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  crewDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle'
  },
  guidanceCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.grey[50]
  },
  guidanceHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  guidanceTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  stepperRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    marginTop: theme.spacing(2),
    flexWrap: 'wrap'
  },
  stepBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    borderRadius: 20,
    fontSize: '0.8rem',
    fontWeight: 500,
    border: `1px solid ${theme.palette.grey[300]}`,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.common.white,
    whiteSpace: 'nowrap'
  },
  stepActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600
  },
  stepComplete: {
    borderColor: theme.palette.success.main || '#4caf50',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32'
  },
  stepConnector: {
    width: 24,
    height: 2,
    backgroundColor: theme.palette.grey[300],
    flexShrink: 0
  },
  sectorGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75)
  },
  sectorChip: {
    minWidth: 40,
    fontWeight: 600,
    fontSize: '0.75rem'
  },
  sectionHeader: {
    backgroundColor: theme.palette.grey[100]
  },
  sectionHeaderCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '10px 10px',
    borderBottom: `2px solid ${theme.palette.grey[300]}`
  },
  anchorRow: {
    borderLeft: `4px solid ${theme.palette.success.main || '#4caf50'}`
  },
  reasonChip: {
    margin: 2,
    height: 24,
    fontSize: '0.72rem'
  },
  summaryLine: {
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  mapSection: {
    marginBottom: theme.spacing(2)
  },
  mapToggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    cursor: 'pointer'
  },
  mapLegend: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: '6px 12px',
    alignItems: 'center'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.78rem'
  },
  selectedRow: {
    backgroundColor: '#e3f2fd',
    cursor: 'pointer'
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  // --- Layout: main + inline side panel ---
  outerLayout: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start'
  },
  mainColumn: {
    flex: 1,
    minWidth: 0
  },
  sidePanel: {
    width: 340,
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    maxHeight: '100vh',
    overflowY: 'auto'
  },
  sidePanelHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(1)
  },
  sidePanelBody: {
    padding: theme.spacing(0, 2, 2, 2)
  },
  sidePanelSection: {
    marginBottom: theme.spacing(2)
  },
  sidePanelLabel: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5)
  },
  sidePanelActions: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 2, 2)
  },
  scoreBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: '50%',
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#fff'
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    fontSize: '0.82rem'
  },
  statLabel: {
    color: theme.palette.text.secondary
  },
  statValue: {
    fontWeight: 500
  }
}))

export default function AnchorsTab ({
  url,
  selectedScenarioId,
  crews,
  crewIds,
  routes,
  routeIds,
  onSnackbar,
  onRefreshMetrics
}) {
  const classes = useStyles()

  const [anchorCandidates, setAnchorCandidates] = useState([])
  const [anchorCoverage, setAnchorCoverage] = useState(null)
  const [anchorViewCounts, setAnchorViewCounts] = useState({ suggestedAnchors: 0, allWeeklyProperties: 0, allProperties: 0 })
  const [loadingAnchors, setLoadingAnchors] = useState(false)
  const [runningAnchorScoring, setRunningAnchorScoring] = useState(false)
  const [runningCentroids, setRunningCentroids] = useState(false)
  const [savingAnchorByPropertyId, setSavingAnchorByPropertyId] = useState({})
  const [anchorCrewChoiceByPropertyId, setAnchorCrewChoiceByPropertyId] = useState({})
  const [guideOpen, setGuideOpen] = useState(true)
  const [mapOpen, setMapOpen] = useState(true)
  const [mapRef, setMapRef] = useState(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState(null)
  const [anchorViewMode, setAnchorViewMode] = useState(ANCHOR_VIEW_MODES.suggested)

  const fetchAnchorCandidates = useCallback(async (scenarioId) => {
    setLoadingAnchors(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/anchors/candidates`, {
        params: { scenarioId: scenarioId || undefined, candidateOnly: false, limit: 5000 }
      })
      const nextRows = response.data?.rows || []
      setAnchorCandidates(nextRows)
      setAnchorCoverage(response.data?.coverage || null)
      setAnchorViewCounts(response.data?.viewCounts || {
        suggestedAnchors: nextRows.filter(item => isSuggestedAnchorViewItem(item)).length,
        allWeeklyProperties: nextRows.filter(item => isWeeklyAnchorEligible(item)).length,
        allProperties: nextRows.length
      })
      setAnchorCrewChoiceByPropertyId(nextRows.reduce((acc, item) => {
        if (item.isAnchor) {
          acc[item.aspirePropertyId] = item.anchorCrewId || item.suggestedCrewId || ''
        }
        return acc
      }, {}))
    } catch (err) {
      console.error('Error loading anchor candidates:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to load anchor candidates', 'error')
      setAnchorCandidates([])
      setAnchorCoverage(null)
      setAnchorViewCounts({ suggestedAnchors: 0, allWeeklyProperties: 0, allProperties: 0 })
    } finally {
      setLoadingAnchors(false)
    }
  }, [url, onSnackbar])

  useEffect(() => {
    if (!selectedScenarioId) {
      setAnchorCandidates([])
      setAnchorCoverage(null)
      setAnchorViewCounts({ suggestedAnchors: 0, allWeeklyProperties: 0, allProperties: 0 })
      setAnchorCrewChoiceByPropertyId({})
      return
    }
    fetchAnchorCandidates(selectedScenarioId)
  }, [selectedScenarioId, fetchAnchorCandidates])

  const runAnchorScoring = async () => {
    setRunningAnchorScoring(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/compute-anchors`, {}, {
        params: { scenarioId: selectedScenarioId || undefined }
      })
      const scored = response.data?.scored || 0
      const candidateCount = response.data?.candidates || 0
      onSnackbar(`Anchor scoring complete: ${candidateCount} candidates across ${scored} properties`, 'success')
      await fetchAnchorCandidates(selectedScenarioId)
    } catch (err) {
      console.error('Error computing anchors:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to compute anchors', 'error')
    } finally {
      setRunningAnchorScoring(false)
    }
  }

  const runCentroidCompute = async () => {
    setRunningCentroids(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/compute-centroids`, {})
      const updatedCount = response.data?.updatedCount || 0
      onSnackbar(`Crew centroids updated (${updatedCount} crews)`, 'success')
      if (onRefreshMetrics) onRefreshMetrics()
    } catch (err) {
      console.error('Error computing centroids:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to compute centroids', 'error')
    } finally {
      setRunningCentroids(false)
    }
  }

  const saveAnchor = async (candidate, isAnchor, crewId) => {
    const propertyId = candidate?.aspirePropertyId
    if (!propertyId) return
    if (isAnchor && !isWeeklyAnchorEligible(candidate)) {
      onSnackbar('Only weekly properties can be anchors', 'warning')
      return
    }
    setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: true }))
    try {
      const body = { isAnchor: !!isAnchor }
      if (crewId) body.anchorCrewId = crewId
      const response = await axios.patch(`${url}/api/hmi/territory/anchors/${propertyId}`, body)
      const updatedCoverage = response.data?.coverage || null
      if (updatedCoverage) setAnchorCoverage(updatedCoverage)
      onSnackbar(isAnchor ? 'Anchor promoted' : 'Anchor removed', 'success')
      await fetchAnchorCandidates(selectedScenarioId)
    } catch (err) {
      console.error('Error saving anchor:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to save anchor', 'error')
    } finally {
      setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: false }))
    }
  }

  const saveCrewAssignment = async (item) => {
    const propertyId = item?.aspirePropertyId
    if (!propertyId) return
    const crewId = anchorCrewChoiceByPropertyId[propertyId] || ''
    if (!crewId) {
      onSnackbar('Select a crew first', 'warning')
      return
    }
    setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: true }))
    try {
      const response = await axios.patch(`${url}/api/hmi/territory/anchors/${propertyId}`, {
        isAnchor: true,
        anchorCrewId: crewId
      })
      const updatedCoverage = response.data?.coverage || null
      if (updatedCoverage) setAnchorCoverage(updatedCoverage)
      onSnackbar('Crew assigned', 'success')
      await fetchAnchorCandidates(selectedScenarioId)
    } catch (err) {
      console.error('Error assigning crew:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to assign crew', 'error')
    } finally {
      setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: false }))
    }
  }

  const visibleRows = useMemo(() => {
    if (anchorViewMode === ANCHOR_VIEW_MODES.suggested) {
      return anchorCandidates.filter(isSuggestedAnchorViewItem)
    }
    if (anchorViewMode === ANCHOR_VIEW_MODES.weekly) {
      return anchorCandidates.filter(item => isWeeklyAnchorEligible(item))
    }
    return anchorCandidates
  }, [anchorCandidates, anchorViewMode])

  const allActiveAnchors = useMemo(() => anchorCandidates.filter(item => item.isAnchor), [anchorCandidates])
  const activeAnchors = useMemo(() => visibleRows.filter(item => item.isAnchor), [visibleRows])
  const candidates = useMemo(() => visibleRows.filter(item => !item.isAnchor), [visibleRows])

  const currentStep = useMemo(() => {
    if (allActiveAnchors.length > 0 && allActiveAnchors.every(a => a.anchorCrewId)) return 3
    if (allActiveAnchors.length > 0) return 2
    if (anchorCandidates.length > 0) return 1
    return 0
  }, [allActiveAnchors, anchorCandidates.length])

  const coverageSummaryText = useMemo(() => {
    if (!anchorCoverage) return null
    const count = anchorCoverage.anchorCount || 0
    const covered = (anchorCoverage.coveredSectors || []).length
    const missing = (anchorCoverage.missingSectors || []).length
    if (missing === 0) return `${count} anchor${count !== 1 ? 's' : ''} across all 8 sectors \u2014 full coverage`
    return `${count} anchor${count !== 1 ? 's' : ''} across ${covered} of 8 sectors \u2014 ${missing} gap${missing !== 1 ? 's' : ''} remaining`
  }, [anchorCoverage])

  const mappableItems = useMemo(() =>
    visibleRows.filter(item =>
      Number.isFinite(item.latitude) && Number.isFinite(item.longitude)
    ), [visibleRows])

  const yardPosition = useMemo(() => {
    if (!anchorCoverage?.yard) return null
    const { latitude, longitude } = anchorCoverage.yard
    return Number.isFinite(latitude) && Number.isFinite(longitude)
      ? { lat: latitude, lng: longitude } : null
  }, [anchorCoverage])

  useEffect(() => {
    if (!mapRef || !mappableItems.length || !window.google?.maps) return
    const bounds = new window.google.maps.LatLngBounds()
    mappableItems.forEach(item => bounds.extend({ lat: item.latitude, lng: item.longitude }))
    if (yardPosition) bounds.extend(yardPosition)
    if (!bounds.isEmpty()) mapRef.fitBounds(bounds, 50)
  }, [mapRef, mappableItems, yardPosition])

  useEffect(() => {
    if (!selectedPropertyId) return
    if (visibleRows.some(item => item.aspirePropertyId === selectedPropertyId)) return
    setSelectedPropertyId(null)
  }, [selectedPropertyId, visibleRows])

  const getAnchorMarkerIcon = useCallback((item) => {
    const isSelected = item.aspirePropertyId === selectedPropertyId
    const baseScale = item.isAnchor ? 12 : getCandidateMarkerScale(item.anchorScore)
    let fillColor = getCandidateMarkerColor(item.anchorScore)
    let fillOpacity = 0.82
    let strokeColor = '#fff'
    let strokeWeight = 1.5

    if (item.isAnchor) {
      const crewId = anchorCrewChoiceByPropertyId[item.aspirePropertyId] || item.anchorCrewId || ''
      fillColor = crewId ? getCrewColor(crewId, crewIds) : '#4caf50'
      fillOpacity = 0.95
    } else if (item.suppressedByNearbyHigher) {
      fillOpacity = 0.42
      strokeColor = '#6d4c41'
      strokeWeight = 2
    }

    return {
      path: window.google?.maps?.SymbolPath?.CIRCLE,
      scale: isSelected ? baseScale + 4 : baseScale,
      fillColor,
      fillOpacity,
      strokeColor: isSelected ? '#1976D2' : strokeColor,
      strokeWeight: isSelected ? 3 : strokeWeight
    }
  }, [crewIds, anchorCrewChoiceByPropertyId, selectedPropertyId])

  const isLoading = loadingAnchors || runningAnchorScoring || runningCentroids

  if (!selectedScenarioId) {
    return (
      <div>
        <Alert severity="info">
          Select a scenario above to view and manage anchor properties. Anchors are high-priority properties that serve as geographic reference points for crew territory assignments.
        </Alert>
      </div>
    )
  }

  const coveredSectors = anchorCoverage?.coveredSectors || []
  const suggestedCount = Number(anchorViewCounts?.suggestedAnchors || 0)
  const allWeeklyCount = Number(anchorViewCounts?.allWeeklyProperties || 0)
  const allPropertiesCount = Number(anchorViewCounts?.allProperties || anchorCandidates.length || 0)
  const visibleCandidatesTitle = anchorViewMode === ANCHOR_VIEW_MODES.suggested
    ? 'Suggested Candidates'
    : (anchorViewMode === ANCHOR_VIEW_MODES.weekly ? 'Weekly Properties (non-anchor)' : 'Properties (non-anchor)')

  const selectProperty = (aspirePropertyId) => {
    setSelectedPropertyId(prev => prev === aspirePropertyId ? null : aspirePropertyId)
  }

  const selectedItem = selectedPropertyId
    ? visibleRows.find(c => c.aspirePropertyId === selectedPropertyId)
    : null

  // --- Render a CANDIDATE row (Phase 1) ---
  const renderCandidateRow = (item) => {
    const isSelected = item.aspirePropertyId === selectedPropertyId
    return (
      <TableRow
        key={item.aspirePropertyId}
        id={`anchor-row-${item.aspirePropertyId}`}
        className={isSelected ? classes.selectedRow : classes.clickableRow}
        onClick={() => selectProperty(item.aspirePropertyId)}
      >
        <TableCell className={classes.cell}>
          <Typography variant="body2">{item.propertyName}</Typography>
          <Typography className={classes.addressText}>Aspire #{item.aspirePropertyId}</Typography>
          <Typography className={classes.addressText}>
            Freq: {getFrequencyLabel(item)}
            {!isWeeklyAnchorEligible(item) ? ' • Not anchor-eligible' : ''}
          </Typography>
          {item.suppressedByNearbyHigher && (
            <Typography className={classes.addressText}>
              Suppressed in Suggested view (near #{item.suppressedByAspirePropertyId}, +{item.suppressedByScoreDelta} pts, {item.suppressedByDistanceMiles} mi)
            </Typography>
          )}
        </TableCell>
        <TableCell className={classes.cell}>{Math.round(item.anchorScore || 0)}</TableCell>
        <TableCell className={classes.cell}>
          {(item.anchorReason || []).length
            ? (item.anchorReason || []).map((code, idx) => {
                const reason = getReasonLabel(code)
                return (
                  <Tooltip key={idx} title={reason.tooltip} arrow>
                    <Chip size="small" label={reason.label} className={classes.reasonChip} variant="outlined" />
                  </Tooltip>
                )
              })
            : '-'}
        </TableCell>
      </TableRow>
    )
  }

  // --- Render an ACTIVE ANCHOR row (Phase 2) ---
  const renderAnchorRow = (item) => {
    const anchorChoice = anchorCrewChoiceByPropertyId[item.aspirePropertyId] || item.anchorCrewId || ''
    const crewColor = anchorChoice ? getCrewColor(anchorChoice, crewIds) : '#ccc'
    const isSelected = item.aspirePropertyId === selectedPropertyId
    return (
      <TableRow
        key={item.aspirePropertyId}
        id={`anchor-row-${item.aspirePropertyId}`}
        className={`${classes.anchorRow} ${isSelected ? classes.selectedRow : classes.clickableRow}`}
        onClick={() => selectProperty(item.aspirePropertyId)}
      >
        <TableCell className={classes.cell}>
          <Typography variant="body2">{item.propertyName}</Typography>
          <Typography className={classes.addressText}>
            Aspire #{item.aspirePropertyId}
          </Typography>
          <Typography className={classes.addressText}>
            Freq: {getFrequencyLabel(item)}
          </Typography>
        </TableCell>
        <TableCell className={classes.cell}>{Math.round(item.anchorScore || 0)}</TableCell>
        <TableCell className={classes.cell}>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <span className={classes.crewDot} style={{ backgroundColor: crewColor }} />
            {anchorChoice
              ? (crews.find(c => c._id === anchorChoice)?.name || 'Assigned')
              : <span style={{ color: '#e65100', fontSize: '0.8rem' }}>Needs crew</span>
            }
          </Box>
        </TableCell>
      </TableRow>
    )
  }

  // --- Score color helper ---
  const getScoreColor = (score) => {
    if (score >= 15) return '#2e7d32'
    if (score >= 10) return '#1565c0'
    if (score >= 5) return '#e65100'
    return '#9e9e9e'
  }

  // --- Inline side panel content ---
  const renderSidePanel = () => {
    if (!selectedItem) return null
    const saving = !!savingAnchorByPropertyId[selectedItem.aspirePropertyId]
    const anchorChoice = anchorCrewChoiceByPropertyId[selectedItem.aspirePropertyId] || selectedItem.anchorCrewId || ''
    const crewColor = anchorChoice ? getCrewColor(anchorChoice, crewIds) : '#ccc'
    const score = Math.round(selectedItem.anchorScore || 0)
    const weeklyEligible = isWeeklyAnchorEligible(selectedItem)
    return (
      <Paper variant="outlined" className={classes.sidePanel}>
        <div className={classes.sidePanelHeader}>
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.3 }}>
              {selectedItem.propertyName}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
              Aspire #{selectedItem.aspirePropertyId}
            </Typography>
          </div>
          <IconButton size="small" onClick={() => setSelectedPropertyId(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <Divider />

        <div className={classes.sidePanelBody}>
          {/* Score + Status */}
          <div className={classes.sidePanelSection} style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className={classes.scoreBadge} style={{ backgroundColor: getScoreColor(score) }}>
                {score}
              </div>
              <div>
                <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.82rem' }}>
                  Anchor Score
                </Typography>
                <Chip
                  size="small"
                  label={selectedItem.isAnchor ? 'Active Anchor' : 'Candidate'}
                  style={{
                    backgroundColor: selectedItem.isAnchor ? '#e8f5e9' : '#e3f2fd',
                    color: selectedItem.isAnchor ? '#2e7d32' : '#1565c0',
                    fontWeight: 600,
                    marginTop: 2,
                    height: 22,
                    fontSize: '0.72rem'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Reasons */}
          {(selectedItem.anchorReason || []).length > 0 && (
            <div className={classes.sidePanelSection}>
              <Typography className={classes.sidePanelLabel}>Why this scored high</Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(selectedItem.anchorReason || []).map((code, idx) => {
                  const reason = getReasonLabel(code)
                  return (
                    <Tooltip key={idx} title={reason.tooltip} arrow>
                      <Chip size="small" label={reason.label} variant="outlined" style={{ fontSize: '0.72rem', height: 24 }} />
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className={classes.sidePanelSection}>
            <Typography className={classes.sidePanelLabel}>Details</Typography>
            <div className={classes.statRow}>
              <span className={classes.statLabel}>Frequency</span>
              <span className={classes.statValue}>{getFrequencyLabel(selectedItem)}</span>
            </div>
            <div className={classes.statRow}>
              <span className={classes.statLabel}>Annual visits</span>
              <span className={classes.statValue}>{selectedItem.annualOccurrences || 0}</span>
            </div>
            <div className={classes.statRow}>
              <span className={classes.statLabel}>Median on-site</span>
              <span className={classes.statValue}>
                {selectedItem.medianOnSiteHours > 0
                  ? (() => {
                    const wallMin = selectedItem.medianOnSiteMinutes || Math.round(selectedItem.medianOnSiteHours / 3 * 60)
                    return `${(wallMin / 60).toFixed(1)} hrs (${selectedItem.medianOnSiteHours.toFixed(1)} man hrs)`
                  })()
                  : '\u2014'
                }
              </span>
            </div>
            <div className={classes.statRow}>
              <span className={classes.statLabel}>Coordinates</span>
              <span className={classes.statValue}>
                {Number.isFinite(selectedItem.latitude) ? 'Yes' : 'No'}
              </span>
            </div>
          </div>

          {!weeklyEligible && (
            <div className={classes.sidePanelSection}>
              <Alert severity="warning">
                Anchors are restricted to weekly properties. This property is {getFrequencyLabel(selectedItem)}.
              </Alert>
            </div>
          )}

          {!!selectedItem.suppressedByNearbyHigher && (
            <div className={classes.sidePanelSection}>
              <Alert severity="info">
                Hidden from Suggested Anchors because #{selectedItem.suppressedByAspirePropertyId} is {selectedItem.suppressedByScoreDelta} points higher within {selectedItem.suppressedByDistanceMiles} mile(s).
              </Alert>
            </div>
          )}

          {/* Crew assignment — only for active anchors */}
          {selectedItem.isAnchor && (
            <div className={classes.sidePanelSection}>
              <Typography className={classes.sidePanelLabel}>Crew Assignment</Typography>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <span className={classes.crewDot} style={{ backgroundColor: crewColor, width: 12, height: 12 }} />
                <Select
                  value={anchorChoice}
                  onChange={(e) => {
                    setAnchorCrewChoiceByPropertyId(prev => ({
                      ...prev,
                      [selectedItem.aspirePropertyId]: e.target.value
                    }))
                  }}
                  variant="outlined"
                  size="small"
                  className={classes.crewSelect}
                  disabled={saving}
                  style={{ flex: 1 }}
                >
                  <MenuItem value="" disabled>Select crew</MenuItem>
                  {crews.map(crew => (
                    <MenuItem key={crew._id} value={crew._id}>{crew.name}</MenuItem>
                  ))}
                </Select>
              </Box>
            </div>
          )}
        </div>

        <Divider />

        {/* Actions */}
        <div className={classes.sidePanelActions} style={{ paddingTop: 12 }}>
          {selectedItem.isAnchor ? (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={saving || !anchorChoice || !weeklyEligible}
                onClick={() => saveCrewAssignment(selectedItem)}
                style={{ flex: 1 }}
              >
                {saving ? 'Saving...' : (weeklyEligible ? 'Save Crew' : 'Weekly Only')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={saving}
                onClick={() => saveAnchor(selectedItem, false)}
                style={{ flex: 1 }}
              >
                Demote
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={saving || !weeklyEligible}
              onClick={() => saveAnchor(selectedItem, true)}
              fullWidth
            >
              {saving ? 'Saving...' : (weeklyEligible ? 'Promote to Anchor' : 'Weekly Only')}
            </Button>
          )}
        </div>
      </Paper>
    )
  }

  return (
    <div className={classes.outerLayout}>
      {/* Main content column */}
      <div className={classes.mainColumn}>
        {/* Guidance Card */}
        <Paper variant="outlined" className={classes.guidanceCard}>
          <div className={classes.guidanceHeader} onClick={() => setGuideOpen(prev => !prev)}>
            <div className={classes.guidanceTitle}>
              <HelpOutlineIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2">How Anchors Work</Typography>
            </div>
            <IconButton size="small">
              {guideOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          <Collapse in={guideOpen}>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Anchors are high-priority properties that define the geographic center of each crew's territory.
              First, score and promote the best candidates to anchors based on their criteria.
              Then, assign each anchor to a crew and compute centroids to optimize territory boundaries.
            </Typography>
            <div className={classes.stepperRow}>
              {WORKFLOW_STEPS.map((step, idx) => {
                let stepClass = classes.stepBox
                if (step.num < currentStep) stepClass += ' ' + classes.stepComplete
                else if (step.num === currentStep) stepClass += ' ' + classes.stepActive
                return (
                  <React.Fragment key={step.num}>
                    {idx > 0 && <div className={classes.stepConnector} />}
                    <div className={stepClass}>
                      {step.num < currentStep && <CheckCircleIcon style={{ fontSize: 16, marginRight: 4 }} />}
                      {step.num}. {step.label}
                    </div>
                  </React.Fragment>
                )
              })}
            </div>
          </Collapse>
        </Paper>

        {/* Controls */}
        <Paper variant="outlined" className={classes.controlsCard}>
          <div className={classes.controlsRow}>
            <Tooltip title="Analyze all properties and assign anchor scores based on 7 criteria" arrow>
              <span>
                <Button variant="outlined" size="small" disabled={runningAnchorScoring} onClick={runAnchorScoring}>
                  {runningAnchorScoring ? 'Scoring...' : 'Score Candidates'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Calculate the weighted geographic center of each crew's anchors" arrow>
              <span>
                <Button variant="outlined" size="small" disabled={runningCentroids} onClick={runCentroidCompute}>
                  {runningCentroids ? 'Computing...' : 'Compute Centroids'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Reload anchor data" arrow>
              <span>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} disabled={loadingAnchors} onClick={() => fetchAnchorCandidates(selectedScenarioId)}>
                  Refresh
                </Button>
              </span>
            </Tooltip>
            {isLoading && <CircularProgress size={18} />}
          </div>

          <div className={classes.controlsRow}>
            <Typography variant="body2" color="textSecondary">View:</Typography>
            <Button
              size="small"
              variant={anchorViewMode === ANCHOR_VIEW_MODES.suggested ? 'contained' : 'outlined'}
              color={anchorViewMode === ANCHOR_VIEW_MODES.suggested ? 'primary' : 'default'}
              onClick={() => setAnchorViewMode(ANCHOR_VIEW_MODES.suggested)}
            >
              Suggested Anchors ({suggestedCount})
            </Button>
            <Button
              size="small"
              variant={anchorViewMode === ANCHOR_VIEW_MODES.weekly ? 'contained' : 'outlined'}
              color={anchorViewMode === ANCHOR_VIEW_MODES.weekly ? 'primary' : 'default'}
              onClick={() => setAnchorViewMode(ANCHOR_VIEW_MODES.weekly)}
            >
              All Weekly Properties ({allWeeklyCount})
            </Button>
            <Button
              size="small"
              variant={anchorViewMode === ANCHOR_VIEW_MODES.all ? 'contained' : 'outlined'}
              color={anchorViewMode === ANCHOR_VIEW_MODES.all ? 'primary' : 'default'}
              onClick={() => setAnchorViewMode(ANCHOR_VIEW_MODES.all)}
            >
              All Properties ({allPropertiesCount})
            </Button>
            <Chip size="small" variant="outlined" label={`Showing ${visibleRows.length}`} />
          </div>

          {anchorCoverage && (
            <>
              <div className={classes.sectorGrid}>
                {ALL_SECTORS.map(sector => {
                  const isCovered = coveredSectors.includes(sector)
                  return (
                    <Chip
                      key={sector}
                      size="small"
                      label={sector}
                      className={classes.sectorChip}
                      style={{
                        backgroundColor: isCovered ? '#e8f5e9' : '#ffebee',
                        color: isCovered ? '#2e7d32' : '#c62828',
                        borderColor: isCovered ? '#a5d6a7' : '#ef9a9a'
                      }}
                      variant="outlined"
                    />
                  )
                })}
              </div>
              {coverageSummaryText && (
                <Typography className={classes.summaryLine}>{coverageSummaryText}</Typography>
              )}
            </>
          )}
        </Paper>

        {/* Map Panel */}
        <Paper variant="outlined" className={classes.mapSection}>
          <div className={classes.mapToggleRow} onClick={() => setMapOpen(p => !p)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2">Map View</Typography>
              <Chip size="small" label={`${mappableItems.length} mapped`} variant="outlined" />
            </div>
            <IconButton size="small">
              {mapOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>

          <Collapse in={mapOpen}>
            {process.env.REACT_APP_GMAPKEY ? (
              <LoadScript googleMapsApiKey={process.env.REACT_APP_GMAPKEY}>
                <GoogleMap
                  mapContainerStyle={ANCHOR_MAP_STYLE}
                  center={DEFAULT_CENTER}
                  zoom={DEFAULT_ZOOM}
                  onLoad={map => setMapRef(map)}
                  options={{ mapTypeControl: true, streetViewControl: false, fullscreenControl: true }}
                >
                  {mappableItems.map(item => (
                    <Marker
                      key={item.aspirePropertyId}
                      position={{ lat: item.latitude, lng: item.longitude }}
                      icon={getAnchorMarkerIcon(item)}
                      title={item.propertyName}
                      onClick={() => selectProperty(item.aspirePropertyId)}
                    />
                  ))}

                  {yardPosition && (
                    <Marker
                      position={yardPosition}
                      icon={{
                        path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW,
                        scale: 8, fillColor: '#000', fillOpacity: 1,
                        strokeColor: '#FFD600', strokeWeight: 2
                      }}
                      title="Yard"
                    />
                  )}
                </GoogleMap>
              </LoadScript>
            ) : (
              <Typography color="textSecondary" style={{ padding: 16 }}>
                Google Maps API key not configured
              </Typography>
            )}

            <div className={classes.mapLegend}>
              <div className={classes.legendItem}>
                <span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#4caf50', display: 'inline-block' }} />
                Active anchor
              </div>
              <div className={classes.legendItem}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getCandidateMarkerColor(18), display: 'inline-block' }} />
                Candidate 18+
              </div>
              <div className={classes.legendItem}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getCandidateMarkerColor(14), display: 'inline-block' }} />
                Candidate 14-17
              </div>
              <div className={classes.legendItem}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getCandidateMarkerColor(10), display: 'inline-block' }} />
                Candidate 10-13
              </div>
              <div className={classes.legendItem}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9e9e9e', opacity: 0.42, border: '2px solid #6d4c41', display: 'inline-block' }} />
                Suppressed nearby
              </div>
              <div className={classes.legendItem}>
                <span style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '12px solid #000', display: 'inline-block' }} />
                Yard
              </div>
            </div>
          </Collapse>
        </Paper>

        {/* Active Anchors Table */}
        {activeAnchors.length > 0 && (
          <Paper variant="outlined" style={{ marginBottom: 16 }}>
            <div className={classes.tableWrapper}>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={`${classes.cell} ${classes.headerCell}`}>Property</TableCell>
                    <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 60 }}>Score</TableCell>
                    <TableCell className={`${classes.cell} ${classes.headerCell}`}>Crew</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow className={classes.sectionHeader}>
                    <TableCell colSpan={3} className={classes.sectionHeaderCell}>
                      Active Anchors ({activeAnchors.length})
                      {activeAnchors.filter(a => !a.anchorCrewId).length > 0 && (
                        <span style={{ fontWeight: 400, fontSize: '0.78rem', marginLeft: 8, color: '#e65100' }}>
                          &mdash; {activeAnchors.filter(a => !a.anchorCrewId).length} need crew assignment
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                  {activeAnchors.map(item => renderAnchorRow(item))}
                </TableBody>
              </Table>
            </div>
          </Paper>
        )}

        {/* Candidates Table */}
        <Paper variant="outlined">
          <div className={classes.tableWrapper}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={`${classes.cell} ${classes.headerCell}`}>Property</TableCell>
                  <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 60 }}>Score</TableCell>
                  <TableCell className={`${classes.cell} ${classes.headerCell}`}>Reasons</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.length > 0 && (
                  <>
                    <TableRow className={classes.sectionHeader}>
                      <TableCell colSpan={3} className={classes.sectionHeaderCell}>
                        {visibleCandidatesTitle} ({candidates.length})
                      </TableCell>
                    </TableRow>
                    {candidates.slice(0, 30).map(item => renderCandidateRow(item))}
                  </>
                )}

                {!loadingAnchors && visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="textSecondary" style={{ padding: 16 }}>
                        No properties match this view. Try another filter mode or click &lsquo;Score Candidates&rsquo;.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Paper>
      </div>

      {/* Inline side panel — appears when a property is selected */}
      {renderSidePanel()}
    </div>
  )
}
